import { styled } from "goober";
import { useQuery } from "@rocicorp/zero/react";
import { type CSSProperties, type ReactNode, useState } from "react";
import { CONTEXT_LIMIT_MESSAGE } from "../../../shared/contextLimit";
import { queries } from "../../../shared/queries";
import { SUB_AGENT_UPDATE_PREFIX } from "../../../shared/subAgent";
import { Icon, type IconName } from "../Icon";
import { Text } from "../Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import { SkillJson } from "../../tools/UseSkill";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedContent,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";

const systemMessageIconByPrefix: [prefix: string, icon: IconName][] = [
  ["Saved:", "save"],
  ["Restored", "history"],
  ["Workflow renamed", "pencil"],
];

export function systemMessageIcon(text: string): IconName {
  if (text === CONTEXT_LIMIT_MESSAGE) return "brain";
  if (text.startsWith(SUB_AGENT_UPDATE_PREFIX)) return "3b-mark";
  for (const [prefix, icon] of systemMessageIconByPrefix) {
    if (text.startsWith(prefix)) return icon;
  }
  return "info";
}

const Pill = styled("div")`
  display: flex;
  align-items: center;
  height: 28px;
  gap: 4px;
  padding: 6px 10px 6px 8px;
  border-radius: 8px;
  width: fit-content;
`;

export function SystemNotice({
  icon,
  text,
  style,
}: {
  icon: IconName;
  text: string;
  style?: CSSProperties;
}) {
  return (
    <Pill style={style}>
      <Icon name={icon} size={12} color="var(--text-secondary)" />
      <Text variant="text-small" color="var(--text-secondary)">
        {text}
      </Text>
    </Pill>
  );
}

export function LoadedSkillNotice({ name }: { name: string }) {
  const [skill] = useQuery(queries.skillByName({ name }));
  return (
    <CollapsibleNotice
      icon="skills"
      summary={`Used ${name}`}
      details={
        skill && (
          <SkillJson
            input={{
              name: skill.name,
              description: skill.description,
              content: skill.instructions,
            }}
          />
        )
      }
    />
  );
}

// Workflow agent updates carry the outcome for the model on the lines after the heading.
export function systemMessageText(text: string): string {
  if (text === CONTEXT_LIMIT_MESSAGE) return "Reached the context limit";
  if (text.startsWith(SUB_AGENT_UPDATE_PREFIX))
    return text.slice(SUB_AGENT_UPDATE_PREFIX.length).split("\n")[0]!;
  return text;
}

// Git-push system messages lead with the author's name, so we match the stable
// trailing phrase rather than a prefix.
export function isPushMessage(text: string): boolean {
  return text.split("\n")[0]?.endsWith("pushed from local") ?? false;
}

export function CollapsibleNotice({
  icon,
  summary,
  details,
}: {
  icon: IconName;
  summary: string;
  details: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!details) return <SystemNotice icon={icon} text={summary} />;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: expanded ? 2 : 0,
      }}
    >
      <CollapsibleHeader
        aria-expanded={expanded}
        onPress={() => setExpanded((prev) => !prev)}
      >
        <CollapsibleHeaderInner>
          <ToggleIconContainer aria-hidden>
            <DefaultIcon>
              <Icon name={icon} size={12} color="var(--text-secondary)" />
            </DefaultIcon>
            <HoverIcon>
              <Icon
                name={expanded ? "minus" : "plus"}
                size={12}
                color="var(--text-secondary)"
              />
            </HoverIcon>
          </ToggleIconContainer>
          <Text variant="text-small" color="var(--text-secondary)">
            {summary}
          </Text>
        </CollapsibleHeaderInner>
      </CollapsibleHeader>
      <ExpandWrapper data-expanded={expanded} inert={!expanded}>
        <ExpandInner data-expanded={expanded}>
          <ExpandedContent>
            {typeof details === "string" ? (
              <Text variant="text-small" color="var(--text-secondary)">
                <span
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {details}
                </span>
              </Text>
            ) : (
              details
            )}
          </ExpandedContent>
        </ExpandInner>
      </ExpandWrapper>
    </div>
  );
}

function CollapsibleSystemMessage({
  text,
  icon,
}: {
  text: string;
  icon: IconName;
}) {
  const [summary = "", ...rest] = text.split("\n");
  return (
    <CollapsibleNotice
      icon={icon}
      summary={summary}
      details={rest.filter(Boolean).join("\n")}
    />
  );
}

export function MergeMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="merge" />;
}

export function PushMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="laptop" />;
}

export function GitSyncMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="git-branch" />;
}
